/* @flow */
import { getterFromKeyPath } from 'trustar/utils';
import { createSelector } from 'reselect';
import { Map, List, OrderedMap } from 'immutable';
import createDebugger from 'debug';

const debug = createDebugger('app:modules:reports:getters'); // eslint-disable-line no-unused-vars

import { enclavesMap } from 'modules/enclave/getters';
import { visitedExplore, userLastViewed, lastRoute } from 'modules/app/getters';
import { indicatorList } from 'modules/indicators/getters';
import { encodeId } from 'trustar/utils';
import { LAST_VIEWED_SEARCH, LAST_VIEWED_REPORTS, LAST_VIEWED_INDICATORS } from 'modules/app/constants';

const entitiesSearchData = getterFromKeyPath(['search', 'searchResults', 'entities']);
const reportsSearchData = getterFromKeyPath(['search', 'searchResults', 'reports']);

const _reports = getterFromKeyPath(['reports', 'reports']);
export const reports = createSelector(
  [_reports, enclavesMap],
  (reports, enclaves) => {
    return reports.map(report => {
      const canonicalEnclaves = report
        .getIn(['data', 'enclaves'], List())
        .map(x => x.get('id'))
        .map(id => enclaves.get(id));
      return report.setIn(['data', 'enclaves'], canonicalEnclaves);
    });
  }
);
export const reportList = getterFromKeyPath(['reports', 'reportList']);
export const isFetchingList = getterFromKeyPath(['reports', 'isFetchingList']);
export const displayOptions = getterFromKeyPath(['reports', 'options']);
export const metadataCache = getterFromKeyPath(['reports', 'metadataCache']);
export const currentReportId = getterFromKeyPath(['reports', 'currentReportId']);

export const currentReportMetadata = createSelector(
  [metadataCache, currentReportId],
  (metadata, id) => {
    // Report ID can be an array if it is clicked on the graph
    const reportId = id && Array.isArray(id) ? id[0] : id;
    return metadata.get(reportId, Map());
});

const isEnclaveReport = x => Boolean(x.getIn(['data', 'enclaves']));

// NOTE: It's very important that we sort the enclave IDs. The order isn't
// important other than the fact that they _are ordered_. This is because once
// these enclaves get to the view layer it can causes uneeded rerenders every
// time the order changes, and this can actually happen quite a lot as data is
// fetched from the server. Namely, changing the order of the enclaves casues
// the DetailPanel to rerender because it sees that one of its lists has
// changed.
const toEnclaveIds = x => x.getIn(['data', 'enclaves'], List())
  .map(x => x.get('id'))
  .sort(); // See NOTE

// OrderedMap of report ids to their enclave list. Reports that are not enclave
// reports are omited, which can be used to determine if a report has enclaves
// at all. If not, it won't even be in this collection
export const reportEnclaves = createSelector(
  [ reports, reportList, enclavesMap ],
  (reports, reportList, enclaves) =>
    reports
      .filter(isEnclaveReport)
      .map(toEnclaveIds)
      .merge(
        reportList
          .filter(isEnclaveReport)
          .map(toEnclaveIds)
      )
      .sort()
      .map(x => x.map(id => enclaves.get(id)))
);

export const report = (id: string) => {
  return createSelector(
    reports,
    (reports: OrderedMap<any, any>) => reports.get(id),
  );
};


/**
 * "Merge" in the report pointer (a) with the full report (b). This is why we
 * simply do a setIn on b
 */
const reportMerger = (a, b) => {
  // $FlowIgnore: Issue with immutable types
  return b.setIn(['data', 'type'], a.getIn(['data', 'type']));
};

export const reportSeq = createSelector(
  [reportList, reports, reportEnclaves, metadataCache],
  (
    reportList: OrderedMap<any, any>,
    reports: OrderedMap<any, any>,
    enclavesByReport: Map<string, any>,
    metadata: Map<string, any>,
  ) => {
    const mergableReports = reports.filter(x => reportList.has(x.get('id')));
    return reportList
      .mergeWith(reportMerger, mergableReports) // Merge in any report data that has been fully fetched
      .map(x => {
        // $FlowIgnore
        const _data = x.get('data', Map());
        // $FlowIgnore
        const data = _data.set('loading', x.get('loading'));
        const meta = metadata.get(data.get('id'), Map()); // NOTE: without the default Map this could return null
        return data
          .update(x => meta.merge(data)) // Merge metadata with data, but give data precedence
          .set('enclaves', enclavesByReport.get(data.get('id')));
      })
      .valueSeq();
  }
);

/**
 * NOTE: We check whether the report stored both exists and has an ID. This is
 * because it's possible for report information (such as metadata) to be fetched
 * first and merged into the store, but if that's the case it will not have the
 * full date so we make sure it has an id as an indicator that the data we want
 * is all there.
 */
export const currentReportOrListItem = createSelector(
  [ reports, reportList, currentReportId ],
  (reports: OrderedMap<any, any>, reportList: OrderedMap<any, any>, id: string) => {
    const reportId = id && Array.isArray(id) ? id[0] : id;

    return reports.getIn([reportId, 'data', 'id']) // See NOTE
      ? reports.getIn([reportId, 'data'])
      : reportList.getIn([reportId, 'data']);
  },
);

export const reportOrListItem = (id: string) => {
  return createSelector(
    [ reports, reportList ],
    (reports: OrderedMap<any, any>, reportList: OrderedMap<any, any>) => {
      return reports.getIn([id, 'data', 'id']) // See NOTE
        ? reports.getIn([id, 'data'])
        : reportList.getIn([id, 'data']);
    },
  );
};

/* ID of first Reports List result, should be a traditional incident report
 * which includes correlations so it doesn't break the tour.
 *
 * NOTE: During app load there is no report metadata so we can't determine
 * a report with a positive correlation count. In this case, 'maybeReport'
 * below is undefined so here we just show the first search list result,
 * which may have no correlations.
 *
 */
export const firstReportResultId = createSelector(
  [ reportSeq ],
  (reportSeq) => {
    if (!reportSeq.size) return null;
    const maybeReport = reportSeq
      .filter(r => r.has('reportGuid'))
      .find(r => r.get('correlationCount') > 0);
    return maybeReport
      ? maybeReport.get('reportGuid')
      : reportSeq.toList().first().get('id');
  }
);

/* ID of first Search List result, should be an 'indicator-based' report */
export const firstSearchListResultId = createSelector(
  [ reportList ],
  (reports) => {
    if (!reports.size) return;
    return reports.first().get('id');
  }
);

/* NOTE: on paginating graphs methods below:
 *
 * There is a lot of state that determines what "next" and "prev" mean in terms of a graph.
 *
 * A user can get to a graph from a deep link, the reports list, the indicators list, cases, dashboard,
 * or search results. For deep link, there is no context so next/prev is disabled. We currently
 * are not supporting next/prev iteration for dashboard and cases. For all the others, this
 * getter determines the ID of the next/prev item in the appropriate list.
 */

/* Index must not be -1 (not found) or first or last item in list */
const validIndex = (idx: number): boolean => idx !== -1;
const validatePrev = (idx: number, max: number): boolean => validIndex(idx) && idx > 0;
const validateNext = (idx: number, max: number): boolean => validIndex(idx) && idx < max - 1;

/* Finds index of item in iterable list with matching ID
* Note: Since we now are phasing out multi-enclave submissions, we will get search results
* with the same report for differnet enclaves -- aka duplicates, we need to skip those to avoid a loop.
* TODO: Deal with duplicates */
const getIdxInList = (list: List<any>, id: string): number => {
  let currentIndex = list.findIndex(r => r.get('guid') === id);
  return currentIndex;
};



/* Determine previous graph ID for iterating to previous graph in analysis view, takes a parameter
 * to indicate pagination direction (-1 for previous, +1 for next) */
export const paginateConstellationId = (paginateDirection: number) => createSelector(
  [
    entitiesSearchData,
    reportsSearchData,
    userLastViewed,
    reportList,
    indicatorList,
    currentReportId,
    visitedExplore,
    lastRoute,
  ],
  (
    entitiesSearch: OrderedMap<string, any>,
    reportsSearch: OrderedMap<string, any>,
    lastViewed: string,
    reports: Map<string, any>,
    indicators: Map<string, any>,
    currentId: string,
    visitedExplore: boolean,
    lastRoute: string,
  ): string | null => {

    let result = null;
    let currentIndex;
    let listContext = List();
    const validateIndexInList = paginateDirection === 1 ? validateNext : validatePrev;
    const reportItems = reportsSearch.get('items', List());
    const entityItems = entitiesSearch.get('items', List());

    // Unsupported, short-circuit.
    if (!visitedExplore || lastRoute === '/dashboard' || lastRoute === '/browse/cases') {
      result = null;
    }

    // Handle search context: either reports or IOCs results
    else if (lastViewed === LAST_VIEWED_SEARCH) {
      listContext = List(reportItems);
      currentIndex = getIdxInList(listContext, currentId);
      if (validateIndexInList(currentIndex, listContext.size)) {
        const id = listContext.get(currentIndex + paginateDirection).get('guid');
        result = lastViewed === LAST_VIEWED_INDICATORS ? encodeId(id) : id;
      } else {
        const iocList = List(entityItems);
        currentIndex = iocList.findIndex(ioc => ioc.get('guid') === currentId);
        if (validateIndexInList(currentIndex, iocList.size)) {
          result = encodeId(iocList.get(currentIndex + paginateDirection).get('guid'));
        }
      }
    }

    // Handle reports list context
    else if (lastViewed === LAST_VIEWED_REPORTS) {
      if (lastViewed === LAST_VIEWED_REPORTS) {
        listContext = List(reportItems);
        currentIndex = getIdxInList(listContext, currentId);
        if (validateIndexInList(currentIndex, listContext.size)) {
          result = listContext.get(currentIndex + paginateDirection).get('guid');
        }
      }
    }

    // Handle IOCs list context
    else if (lastViewed === LAST_VIEWED_INDICATORS) {
      if (lastViewed === LAST_VIEWED_INDICATORS) {
        listContext = List(entityItems);
        currentIndex = getIdxInList(listContext, currentId);
        if (validateIndexInList(currentIndex, listContext.size)) {
          result = encodeId(listContext.get(currentIndex + paginateDirection).get('guid'));
        }
      }
    }

    return result;
  }
);

// Copy and Move Report Getters
export const reportActionAsyncInProgress = getterFromKeyPath(['reports', 'reportActionAsyncInProgress']);



// WEBPACK FOOTER //
// ./src/app/modules/reports/getters.js